import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Alert, App, Button, Card, Dropdown, Empty, Space, Tag, Tooltip, Typography } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, CheckSquareOutlined, ClockCircleOutlined, CopyOutlined, DeleteOutlined, EditOutlined, MailOutlined, PlusOutlined, RetweetOutlined, SettingOutlined, ShareAltOutlined } from '@ant-design/icons';
import { SEQUENCE_ACTION_LABELS, STEP_CONDITION_LABELS, replySubject, sequenceStepSchema, stepDays, type SequenceItem, type SequenceStep, type SequenceStepType } from '@crm/shared';
import { errorMessage } from '../../api/client';
import { pctOf, useSaveSteps } from '../../features/sequences/api';
import { IconBadge } from '../../components/StatCard';
import { StepDrawer, STEP_TYPE_LABEL } from './StepDrawer';
import './coldEmail.css';

const ICON: Record<SequenceStepType, ReactNode> = { EMAIL: <MailOutlined />, TASK: <CheckSquareOutlined />, ACTION: <SettingOutlined /> };
const plainText = (html: string) => html.replace(/<\/(p|div|li)>/g, ' ').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/\s+/g, ' ').trim();
const newStep = (type: SequenceStepType, after: number): SequenceStep => sequenceStepSchema.parse({ type, delayDays: after === -1 ? 0 : 3, threadMode: 'reply' });

/** The step list as a timeline. Edits stay local until "Save steps". */
export function StepsTab({ sequence }: { sequence: SequenceItem }) {
  const { message, modal } = App.useApp();
  const save = useSaveSteps();
  const saved = useMemo(() => sequence.steps.map(({ position: _p, stats: _s, ...rest }): SequenceStep => rest), [sequence.steps]);
  const [steps, setSteps] = useState<SequenceStep[]>(saved);
  const [editing, setEditing] = useState<number | null>(null);
  const dirty = JSON.stringify(steps) !== JSON.stringify(saved);

  // a fresh copy from the server (after a save, or someone else's) replaces the list unless there are unsaved edits
  useEffect(() => {
    if (!dirty) setSteps(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved]);

  const days = stepDays(steps);
  const stats = new Map(sequence.steps.map((s) => [s.id, s.stats]));
  const firstEmailIdx = steps.findIndex((s) => s.type === 'EMAIL');
  const running = sequence.status === 'ACTIVE';

  const persist = async (next: SequenceStep[]) => {
    try {
      const r = await save.mutateAsync({ id: sequence.id, steps: next });
      // take the saved copy (new steps now have ids), so the list is clean again
      setSteps(r.steps.map(({ position: _p, stats: _s, ...rest }): SequenceStep => rest));
      message.success('Steps saved');
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const add = (type: SequenceStepType) => {
    const next = [...steps, newStep(type, steps.length - 1)];
    setSteps(next);
    setEditing(next.length - 1);
  };
  const move = (i: number, by: -1 | 1) => {
    const next = [...steps];
    [next[i], next[i + by]] = [next[i + by], next[i]];
    setSteps(next);
  };
  const remove = (i: number) => {
    const s = stats.get(steps[i].id ?? '');
    const go = () => setSteps(steps.filter((_, j) => j !== i));
    if (s && (s.sent || s.waiting))
      modal.confirm({ title: `Delete step ${i + 1}?`, content: `${s.waiting} people are waiting for it; they move on to the step that takes its place. Its stats are removed.`, okText: 'Delete', okButtonProps: { danger: true }, onOk: go });
    else go();
  };

  const addMenu = {
    items: [
      { key: 'EMAIL', icon: <MailOutlined />, label: 'Automatic email' },
      { key: 'TASK', icon: <CheckSquareOutlined />, label: 'Manual task (call, LinkedIn…)' },
      { key: 'ACTION', icon: <SettingOutlined />, label: 'CRM action (tag, list, status…)' },
    ],
    onClick: ({ key }: { key: string }) => add(key as SequenceStepType),
  };

  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      {running && dirty && <Alert type="info" showIcon title="The sequence is running. Saved changes apply to everyone from their next step on; people keep their place by step number." />}
      {dirty && (
        <Alert
          type="warning"
          title="You have unsaved changes to the steps."
          action={
            <Space>
              <Button size="small" onClick={() => setSteps(saved)}>Discard</Button>
              <Button size="small" type="primary" loading={save.isPending} onClick={() => void persist(steps)}>Save steps</Button>
            </Space>
          }
        />
      )}
      {!steps.length && <Card><Empty description="No steps yet. Start with the opening email." /></Card>}
      {steps.map((s, i) => {
        const st = s.id ? stats.get(s.id) : undefined;
        const fwd = s.type === 'EMAIL' && s.forward?.enabled;
        const inThread = s.type === 'EMAIL' && i !== firstEmailIdx && s.threadMode === 'reply' && !fwd;
        const subject = s.type !== 'EMAIL' ? null : inThread ? replySubject(steps[firstEmailIdx]?.subject || '…') : s.subject || (fwd ? `Fwd: ${s.forward?.subject ?? ''}` : '');
        return (
          <div key={s.id ?? `new-${i}`} className="sequence-step">
            <div className="sequence-step-day">
              <Typography.Text strong>Day {days[i]}</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                <ClockCircleOutlined /> {i === 0 && !s.delayDays && !s.delayHours ? 'Right away' : `+${s.delayDays ? `${s.delayDays}d` : ''}${s.delayHours ? ` ${s.delayHours}h` : ''}`}
              </Typography.Text>
            </div>
            <Card size="small" hoverable onClick={() => setEditing(i)} style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <IconBadge icon={ICON[s.type]} size={36} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Space size={6} wrap>
                    <Typography.Text strong>Step {i + 1} · {STEP_TYPE_LABEL[s.type]}</Typography.Text>
                    {inThread && <Tag icon={<RetweetOutlined />}>Same thread</Tag>}
                    {fwd && <Tag icon={<ShareAltOutlined />} color="purple">Forwarded</Tag>}
                    {s.condition !== 'always' && <Tag color="gold">{STEP_CONDITION_LABELS[s.condition].replace('Only if ', 'If ')}</Tag>}
                    {!s.id && <Tag color="blue">New</Tag>}
                  </Space>
                  {s.type === 'EMAIL' ? (
                    <>
                      <div><Typography.Text>{subject || <Typography.Text type="danger">No subject</Typography.Text>}</Typography.Text></div>
                      <Typography.Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ margin: 0 }}>{plainText(s.html) || (fwd ? plainText(s.forward?.html ?? '') : '') || 'Empty email'}</Typography.Paragraph>
                    </>
                  ) : (
                    <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
                      {s.actions.length ? s.actions.map((a) => (a.type === 'create_task' ? `Task: ${a.title || '…'}` : SEQUENCE_ACTION_LABELS[a.type])).join(' · ') : 'No actions yet'}
                    </Typography.Paragraph>
                  )}
                </div>
                {st && s.type === 'EMAIL' && (
                  <Space size={16} className="sequence-step-stats">
                    <Stat label="Sent" value={st.sent} />
                    <Stat label="Opened" value={pctOf(st.opened, st.sent)} />
                    <Stat label="Clicked" value={pctOf(st.clicked, st.sent)} />
                    <Stat label="Replied" value={pctOf(st.replied, st.sent)} />
                  </Space>
                )}
                {st && st.waiting > 0 && (
                  <Tooltip title="People whose next step is this one">
                    <Tag>{st.waiting} waiting</Tag>
                  </Tooltip>
                )}
                <Space size={0} onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Edit"><Button type="text" size="small" icon={<EditOutlined />} aria-label={`Edit step ${i + 1}`} onClick={() => setEditing(i)} /></Tooltip>
                  <Dropdown
                    trigger={['click']}
                    menu={{
                      items: [
                        { key: 'up', icon: <ArrowUpOutlined />, label: 'Move up', disabled: i === 0 },
                        { key: 'down', icon: <ArrowDownOutlined />, label: 'Move down', disabled: i === steps.length - 1 },
                        { key: 'copy', icon: <CopyOutlined />, label: 'Duplicate' },
                        { type: 'divider' },
                        { key: 'delete', icon: <DeleteOutlined />, label: 'Delete', danger: true },
                      ],
                      onClick: ({ key }) => {
                        if (key === 'up') move(i, -1);
                        if (key === 'down') move(i, 1);
                        if (key === 'copy') setSteps([...steps.slice(0, i + 1), { ...s, id: undefined }, ...steps.slice(i + 1)]);
                        if (key === 'delete') remove(i);
                      },
                    }}
                  >
                    <Button type="text" size="small" aria-label={`More for step ${i + 1}`}>⋯</Button>
                  </Dropdown>
                </Space>
              </div>
            </Card>
          </div>
        );
      })}
      <Dropdown trigger={['click']} menu={addMenu}>
        <Button icon={<PlusOutlined />} block type="dashed" style={{ height: 44 }}>Add step</Button>
      </Dropdown>

      <StepDrawer
        open={editing !== null}
        sequence={sequence}
        step={editing !== null ? (steps[editing] ?? null) : null}
        index={editing ?? 0}
        before={editing !== null ? steps.slice(0, editing) : []}
        onClose={() => {
          // a new step closed without filling it in goes away again
          if (editing !== null && !steps[editing]?.id && isBlank(steps[editing])) setSteps(steps.filter((_, j) => j !== editing));
          setEditing(null);
        }}
        onSave={(step) => {
          const next = steps.map((x, j) => (j === editing ? step : x));
          setSteps(next);
          setEditing(null);
          void persist(next);
        }}
      />
    </Space>
  );
}

function isBlank(s: SequenceStep | undefined) {
  return !!s && !s.subject.trim() && !plainText(s.html) && !s.actions.length && !s.forward?.enabled;
}

function Stat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ textAlign: 'center', minWidth: 44 }}>
      <div style={{ fontWeight: 600 }}>{value}</div>
      <Typography.Text type="secondary" style={{ fontSize: 11 }}>{label}</Typography.Text>
    </div>
  );
}
